/**
 * Agregats descriptifs pour la vue « Statistiques ».
 *
 * Purement descriptif : aucune inference, aucun test. C'est le pendant du
 * moteur de correlation — ici on montre ce qui a ete vecu, la on cherche ce qui
 * pourrait l'expliquer.
 */

import { JOUR_MS, dayKeyOf, epoch, shiftDayKey, todayKey } from '../domain/time'
import {
  BRISTOL_DUR_MAX,
  BRISTOL_MOU_MIN,
  type DayKey,
  type Ingredient,
  type Meal,
  type Recipe,
  type Sensation,
  type Stool,
  type SymptomType,
} from '../domain/types'

// ---------------------------------------------------------------------------
// Periode d'observation
// ---------------------------------------------------------------------------

export type Periode = 30 | 90 | 365 | 0

export const PERIODES: { valeur: Periode; libelle: string }[] = [
  { valeur: 30, libelle: '30 jours' },
  { valeur: 90, libelle: '90 jours' },
  { valeur: 365, libelle: '1 an' },
  { valeur: 0, libelle: 'Tout' },
]

export interface Fenetre {
  debut: DayKey
  fin: DayKey
  jours: number
}

/**
 * Fenetre couvrant `jours` jours jusqu'a aujourd'hui, ou tout l'historique si 0.
 *
 * Le parametre est un nombre libre et non le type `Periode` : celui-ci ne decrit
 * que les raccourcis proposes dans l'interface, il n'a pas a brider la fonction.
 */
export function fenetreDe(periode: number, premierJour: DayKey | null): Fenetre {
  const fin = todayKey()
  if (periode === 0) {
    const debut = premierJour ?? fin
    return { debut, fin, jours: nbJours(debut, fin) }
  }
  return { debut: shiftDayKey(fin, -(periode - 1)), fin, jours: periode }
}

function nbJours(debut: DayKey, fin: DayKey): number {
  const a = new Date(`${debut}T12:00:00`).getTime()
  const b = new Date(`${fin}T12:00:00`).getTime()
  return Math.max(1, Math.round((b - a) / JOUR_MS) + 1)
}

function dansFenetre(at: string, f: Fenetre): boolean {
  const j = dayKeyOf(at)
  return j >= f.debut && j <= f.fin
}

// ---------------------------------------------------------------------------
// Passages aux selles par jour
// ---------------------------------------------------------------------------

export interface PointJour {
  date: DayKey
  valeur: number
  /** Libelle court pour l'axe. */
  libelle: string
}

/**
 * Serie **dense** : les jours sans passage valent 0 et ne sont pas omis.
 *
 * Un jour a zero est une information, pas une absence de donnee : le masquer
 * ferait apparaitre un transit regulier la ou il y a eu plusieurs jours sans
 * rien.
 */
export function sellesParJour(selles: readonly Stool[], f: Fenetre): PointJour[] {
  const compte = new Map<DayKey, number>()
  for (const s of selles) {
    if (!dansFenetre(s.at, f)) continue
    const j = dayKeyOf(s.at)
    compte.set(j, (compte.get(j) ?? 0) + 1)
  }

  const out: PointJour[] = []
  let jour = f.debut
  for (let i = 0; i < f.jours; i++) {
    out.push({ date: jour, valeur: compte.get(jour) ?? 0, libelle: jour.slice(8) })
    jour = shiftDayKey(jour, 1)
  }
  return out
}

/**
 * Regroupe une serie quotidienne en semaines si elle est trop dense pour etre
 * lisible. Au-dela d'environ 120 barres, chacune fait moins d'un pixel sur un
 * telephone.
 */
export function agregerSiDense(serie: PointJour[], maxBarres = 120): { serie: PointJour[]; pas: 'jour' | 'semaine' } {
  if (serie.length <= maxBarres) return { serie, pas: 'jour' }
  const out: PointJour[] = []
  for (let i = 0; i < serie.length; i += 7) {
    const bloc = serie.slice(i, i + 7)
    const premier = bloc[0]!
    out.push({
      date: premier.date,
      valeur: bloc.reduce((s, p) => s + p.valeur, 0),
      libelle: premier.date.slice(5).replace('-', '/'),
    })
  }
  return { serie: out, pas: 'semaine' }
}

export interface ResumeSelles {
  total: number
  moyenneParJour: number
  medianeParJour: number
  joursSansPassage: number
  maxParJour: number
}

export function resumeSelles(serie: readonly PointJour[]): ResumeSelles {
  const valeurs = serie.map((p) => p.valeur)
  const total = valeurs.reduce((s, v) => s + v, 0)
  const triees = [...valeurs].sort((a, b) => a - b)
  const m = triees.length >> 1
  const mediane =
    triees.length === 0 ? 0 : triees.length % 2 ? triees[m]! : (triees[m - 1]! + triees[m]!) / 2
  return {
    total,
    moyenneParJour: serie.length ? total / serie.length : 0,
    medianeParJour: mediane,
    joursSansPassage: valeurs.filter((v) => v === 0).length,
    maxParJour: valeurs.length ? Math.max(...valeurs) : 0,
  }
}

// ---------------------------------------------------------------------------
// Repartition de Bristol
// ---------------------------------------------------------------------------

export type CategorieBristol = 'dur' | 'normal' | 'mou'

export function categorieBristol(b: number): CategorieBristol {
  if (b <= BRISTOL_DUR_MAX) return 'dur'
  if (b >= BRISTOL_MOU_MIN) return 'mou'
  return 'normal'
}

export interface BarreBristol {
  bristol: number
  n: number
  categorie: CategorieBristol
}

export function distributionBristol(selles: readonly Stool[], f: Fenetre): BarreBristol[] {
  const compte = [0, 0, 0, 0, 0, 0, 0]
  for (const s of selles) {
    if (!dansFenetre(s.at, f)) continue
    const i = s.bristol - 1
    if (i >= 0 && i < 7) compte[i] = (compte[i] ?? 0) + 1
  }
  return compte.map((n, i) => ({ bristol: i + 1, n, categorie: categorieBristol(i + 1) }))
}

// ---------------------------------------------------------------------------
// Symptomes dans le temps
// ---------------------------------------------------------------------------

export interface PointSymptome {
  t: number
  intensite: number
  /** Libelle de survol. */
  info: string
}

export interface FacetteSymptome {
  type: SymptomType
  points: PointSymptome[]
}

/**
 * Un nuage de points par type de symptome.
 *
 * Le decoupage en facettes n'est pas cosmetique : au-dela de trois series, un
 * nuage colore ne passe plus les seuils de separation pour les daltonismes
 * (cf. la regle de plafond du referentiel de visualisation). L'identite est
 * donc portee par le titre de la facette, jamais par la couleur seule.
 */
export function facettesSymptomes(
  sensations: readonly Sensation[],
  selles: readonly Stool[],
  f: Fenetre,
): FacetteSymptome[] {
  const parType = new Map<SymptomType, PointSymptome[]>()
  const ajouter = (type: SymptomType, t: number, intensite: number, info: string) => {
    const l = parType.get(type)
    if (l) l.push({ t, intensite, info })
    else parType.set(type, [{ t, intensite, info }])
  }

  for (const s of sensations) {
    if (!dansFenetre(s.at, f)) continue
    ajouter(s.type, epoch(s.at), s.intensite, `${s.intensite}/10`)
  }

  // Les passages aux selles portent une intensite implicite : on retient la
  // douleur quand elle est renseignee, et l'ecart a la normale sinon.
  for (const s of selles) {
    if (!dansFenetre(s.at, f)) continue
    const cat = categorieBristol(s.bristol)
    if (cat === 'normal' && !s.urgence && !(s.douleur ?? 0)) continue
    const t = epoch(s.at)
    if (cat === 'mou') ajouter('selles-molles', t, (s.bristol - 4) * 3.3, `Bristol ${s.bristol}`)
    if (cat === 'dur') ajouter('selles-dures', t, (4 - s.bristol) * 3.3, `Bristol ${s.bristol}`)
    if (s.urgence) ajouter('urgence', t, 7, 'Urgence')
    if ((s.douleur ?? 0) > 0) ajouter('douleur-defecation', t, s.douleur!, `Douleur ${s.douleur}/10`)
  }

  return [...parType.entries()]
    .map(([type, points]) => ({ type, points: points.sort((a, b) => a.t - b.t) }))
    .sort((a, b) => b.points.length - a.points.length)
}

// ---------------------------------------------------------------------------
// Repas populaires
// ---------------------------------------------------------------------------

export interface Popularite {
  id: string
  nom: string
  n: number
  /** Part du total, entre 0 et 1. */
  part: number
}

function classer(compte: Map<string, number>, nom: (id: string) => string, limite: number): Popularite[] {
  const total = [...compte.values()].reduce((s, v) => s + v, 0)
  return [...compte.entries()]
    .map(([id, n]) => ({ id, nom: nom(id), n, part: total > 0 ? n / total : 0 }))
    .sort((a, b) => b.n - a.n || a.nom.localeCompare(b.nom, 'fr'))
    .slice(0, limite)
}

export function recettesPopulaires(
  repas: readonly Meal[],
  recettes: readonly Recipe[],
  f: Fenetre,
  limite = 8,
): Popularite[] {
  const noms = new Map(recettes.map((r) => [r.id, r.nom]))
  const compte = new Map<string, number>()
  for (const m of repas) {
    if (!dansFenetre(m.at, f)) continue
    for (const s of m.sources) {
      if (s.kind !== 'recipe') continue
      compte.set(s.recipeId, (compte.get(s.recipeId) ?? 0) + 1)
    }
  }
  return classer(compte, (id) => noms.get(id) ?? 'Recette supprimée', limite)
}

export function ingredientsPopulaires(
  repas: readonly Meal[],
  ingredients: readonly Ingredient[],
  f: Fenetre,
  limite = 10,
): Popularite[] {
  const noms = new Map(ingredients.map((i) => [i.id, i.nom]))
  const compte = new Map<string, number>()
  for (const m of repas) {
    if (!dansFenetre(m.at, f)) continue
    // Un ingredient present deux fois dans un repas ne compte qu'une fois.
    for (const id of new Set(m.ingredientsResolus.map((r) => r.ingredientId))) {
      compte.set(id, (compte.get(id) ?? 0) + 1)
    }
  }
  return classer(compte, (id) => noms.get(id) ?? 'Ingrédient supprimé', limite)
}

/** Repartition des repas par moment de la journee. */
export function repasParType(repas: readonly Meal[], f: Fenetre): Map<string, number> {
  const compte = new Map<string, number>()
  for (const m of repas) {
    if (!dansFenetre(m.at, f)) continue
    compte.set(m.type, (compte.get(m.type) ?? 0) + 1)
  }
  return compte
}
